import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'components/common/Modal';
import SearchableSelect from 'components/common/SearchableSelect';
import api from 'services/api';
import { COMPANY_STORAGE_KEY } from 'types/company.js';
import { FileText, X, Save, Loader2, Upload, Image as ImageIcon, File, CheckCircle, Edit2, Trash2, Paperclip } from 'lucide-react';

const camposObrigatorios = ['id_fornecedor','data_emissao','data_vencimento','valor','id_contas','tipo_pagamento','id_conta','previsao','status'];

function getSelectedCompanyName() {
  try {
    const ctx = localStorage.getItem(COMPANY_STORAGE_KEY);
    if (ctx) {
      const parsed = JSON.parse(ctx);
      if (parsed?.companies?.length > 0) return parsed.companies[0].name;
    }
  } catch {}
  return 'MOV Fibra';
}

export default function ModalCriarContaPagar({ isOpen, onClose, onCreated, canUploadAttachments = false }) {
  const operationKey = useRef({ payload: null, key: null });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    id_fornecedor: '', data_emissao: '', data_vencimento: '', valor: '', id_contas: '',
    tipo_pagamento: '', id_conta: '', previsao: '', documento: '', numero_nota: '', obs: '',
    status: '', chave_pix: '', tipo_pix: '', codigo_barras: ''
  });
  const [arquivos, setArquivos] = useState([]); // [{ file, descricao, base64, error, ready }]
  const [uploadProgress, setUploadProgress] = useState({ total: 0, current: 0, uploading: false });

  // Dropdown data
  const [fornOpts, setFornOpts] = useState([]);
  const [contaOpts, setContaOpts] = useState([]);
  const [planoOpts, setPlanoOpts] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const empresa = useMemo(() => getSelectedCompanyName(), []);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoadingLists(true);
      try {
        const [f, c, p] = await Promise.all([
          api.post('/n8n/adm/list_fornecedores', { empresa }),
          api.post('/n8n/adm/list_contas', { empresa }),
          api.post('/n8n/adm/list_contas_planejamento', { empresa }),
        ]);
        const mapDados = (res, key, labelKey) => {
          const arr = Array.isArray(res.data) ? res.data : [];
          const dados = arr[0]?.dados || [];
          return dados.map((d) => ({ id: d.id, label: String(d[labelKey] || d.nome_razao || '').trim(), cpf_cnpj: d.cpf_cnpj || d.cnpj || d.cpf || null })).filter(o => o.label);
        };
        setFornOpts(mapDados(f, 'dados', 'nome_fantasia'));
        setContaOpts(mapDados(c, 'dados', 'conta'));
        setPlanoOpts(mapDados(p, 'dados', 'planejamento_analitico'));
      } catch (e) {
        setFornOpts([]); setContaOpts([]); setPlanoOpts([]);
      } finally {
        setLoadingLists(false);
      }
    };
    load();
  }, [isOpen, empresa]);

  useEffect(() => {
    if (!isOpen) {
      setForm({ 
        id_fornecedor:'', data_emissao:'', data_vencimento:'', valor:'', id_contas:'', 
        tipo_pagamento:'', id_conta:'', previsao:'', documento:'', numero_nota:'', obs:'',
        status: '', chave_pix: '', tipo_pix: '', codigo_barras: ''
      });
      setArquivos([]);
      setErrors({});
    }
  }, [isOpen]);

  // Máscaras para chave PIX
  const formatarChavePix = (valor, tipoPix) => {
    if (!valor) return '';
    
    const apenasDigitos = String(valor || '').replace(/\D/g, '');
    const valorLimpo = String(valor || '').trim();
    
    switch (tipoPix) {
      case 'CPF':
        // CPF: 11 dígitos - formato XXX.XXX.XXX-XX
        const cpfDigitos = apenasDigitos.slice(0, 11);
        if (cpfDigitos.length <= 3) return cpfDigitos;
        if (cpfDigitos.length <= 6) return `${cpfDigitos.slice(0, 3)}.${cpfDigitos.slice(3)}`;
        if (cpfDigitos.length <= 9) return `${cpfDigitos.slice(0, 3)}.${cpfDigitos.slice(3, 6)}.${cpfDigitos.slice(6)}`;
        return `${cpfDigitos.slice(0, 3)}.${cpfDigitos.slice(3, 6)}.${cpfDigitos.slice(6, 9)}-${cpfDigitos.slice(9, 11)}`;
      
      case 'CNPJ':
        // CNPJ: 14 dígitos - formato XX.XXX.XXX/XXXX-XX
        const cnpjDigitos = apenasDigitos.slice(0, 14);
        if (cnpjDigitos.length <= 2) return cnpjDigitos;
        if (cnpjDigitos.length <= 5) return `${cnpjDigitos.slice(0, 2)}.${cnpjDigitos.slice(2)}`;
        if (cnpjDigitos.length <= 8) return `${cnpjDigitos.slice(0, 2)}.${cnpjDigitos.slice(2, 5)}.${cnpjDigitos.slice(5)}`;
        if (cnpjDigitos.length <= 12) return `${cnpjDigitos.slice(0, 2)}.${cnpjDigitos.slice(2, 5)}.${cnpjDigitos.slice(5, 8)}/${cnpjDigitos.slice(8)}`;
        return `${cnpjDigitos.slice(0, 2)}.${cnpjDigitos.slice(2, 5)}.${cnpjDigitos.slice(5, 8)}/${cnpjDigitos.slice(8, 12)}-${cnpjDigitos.slice(12, 14)}`;
      
      case 'Telefone':
        // Telefone: 10 ou 11 dígitos - formato (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
        const telDigitos = apenasDigitos.slice(0, 11);
        if (telDigitos.length <= 2) return telDigitos;
        if (telDigitos.length <= 6) {
          return `(${telDigitos.slice(0, 2)}) ${telDigitos.slice(2)}`;
        }
        if (telDigitos.length === 10) {
          // 10 dígitos: (XX) XXXX-XXXX
          return `(${telDigitos.slice(0, 2)}) ${telDigitos.slice(2, 6)}-${telDigitos.slice(6, 10)}`;
        }
        // 11 dígitos: (XX) XXXXX-XXXX
        return `(${telDigitos.slice(0, 2)}) ${telDigitos.slice(2, 7)}-${telDigitos.slice(7, 11)}`;
      
      case 'Email':
        // Email: sem formatação especial, apenas validação básica
        return valorLimpo.toLowerCase();
      
      case 'Aleatoria':
        // Chave aleatória: formato UUID geralmente - aceita com ou sem hífens
        // Remove espaços e converte para minúsculas
        return valorLimpo.replace(/\s+/g, '').toLowerCase();
      
      default:
        return valorLimpo;
    }
  };

  // Limpar campos condicionais quando o tipo de pagamento mudar
  useEffect(() => {
    const tipoLower = (form.tipo_pagamento || '').toLowerCase();
    const updates = {};
    
    if (tipoLower !== 'pix' && (form.chave_pix || form.tipo_pix)) {
      updates.chave_pix = '';
      updates.tipo_pix = '';
    }
    
    if (tipoLower !== 'boleto' && form.codigo_barras) {
      updates.codigo_barras = '';
    }
    
    if (Object.keys(updates).length > 0) {
      setForm((prev) => ({ ...prev, ...updates }));
    }
  }, [form.tipo_pagamento, form.chave_pix, form.tipo_pix, form.codigo_barras]);

  // Aplicar formatação quando tipo_pix mudar ou limpar chave_pix se necessário
  useEffect(() => {
    if (form.tipo_pagamento?.toLowerCase() === 'pix' && form.tipo_pix && form.chave_pix) {
      const formatado = formatarChavePix(form.chave_pix, form.tipo_pix);
      if (formatado !== form.chave_pix) {
        setForm((prev) => ({ ...prev, chave_pix: formatado }));
      }
    }
  }, [form.tipo_pagamento, form.tipo_pix, form.chave_pix]);

  const onChavePixKeyDown = (e) => {
    // Interceptar backspace apenas quando o caractere antes do cursor é de formatação
    if (e.key === 'Backspace') {
      const input = e.target;
      const valorAtual = input.value;
      const posicaoCursor = input.selectionStart;
      const posicaoFim = input.selectionEnd;
      
      // Se há seleção, deixar o navegador deletar normalmente
      if (posicaoCursor !== posicaoFim) {
        return;
      }
      
      if (posicaoCursor === 0) return; // Já está no início
      
      const caractereAntes = valorAtual[posicaoCursor - 1];
      
      // Se o caractere antes do cursor é um dígito, deixar o navegador deletar normalmente
      if (/\d/.test(caractereAntes)) {
        return; // Deixa o comportamento padrão do navegador
      }
      
      // Se é um caractere de formatação, interceptar e remover ele + o dígito antes
      e.preventDefault();
      e.stopPropagation();
      
      const antesDoCursor = valorAtual.slice(0, posicaoCursor - 1);
      const depoisDoCursor = valorAtual.slice(posicaoCursor);
      
      // Encontrar o último dígito antes do cursor
      let ultimoDigitoIdx = -1;
      for (let i = antesDoCursor.length - 1; i >= 0; i--) {
        if (/\d/.test(antesDoCursor[i])) {
          ultimoDigitoIdx = i;
          break;
        }
      }
      
      // Se encontrou um dígito, remover ele e o caractere de formatação
      if (ultimoDigitoIdx >= 0) {
        const novoValor = antesDoCursor.slice(0, ultimoDigitoIdx) + depoisDoCursor;
        const formatado = form.tipo_pix ? formatarChavePix(novoValor, form.tipo_pix) : novoValor;
        setForm((p) => ({ ...p, chave_pix: formatado }));
        
        // Ajustar cursor após formatação
        setTimeout(() => {
          if (!input || input.value !== formatado) return;
          const digitosAntes = novoValor.slice(0, ultimoDigitoIdx).replace(/\D/g, '').length;
          let contador = 0;
          let novaPos = 0;
          for (let i = 0; i < formatado.length; i++) {
            if (/\d/.test(formatado[i])) {
              contador++;
              if (contador === digitosAntes) {
                novaPos = i + 1;
                break;
              }
            }
            novaPos = i + 1;
          }
          input.setSelectionRange(Math.min(novaPos, formatado.length), Math.min(novaPos, formatado.length));
        }, 0);
      } else {
        // Se não há dígito antes, apenas remover o caractere de formatação
        const novoValor = antesDoCursor + depoisDoCursor;
        const formatado = form.tipo_pix ? formatarChavePix(novoValor, form.tipo_pix) : novoValor;
        setForm((p) => ({ ...p, chave_pix: formatado }));
      }
    }
  };

  const onChavePixChange = (e) => {
    const input = e.target;
    const valor = input.value;
    const posicaoCursor = input.selectionStart;
    
    // Sempre formatar, independente de ser inserção ou deleção
    const formatado = form.tipo_pix ? formatarChavePix(valor, form.tipo_pix) : valor;
    setForm((p) => ({ ...p, chave_pix: formatado }));
    
    // Ajustar cursor após formatação
    setTimeout(() => {
      if (!input || input.value !== formatado) return;
      // Calcular quantos dígitos existem antes da posição do cursor original
      const digitosAntesCursor = valor.slice(0, posicaoCursor).replace(/\D/g, '').length;
      
      // Encontrar a posição equivalente no texto formatado
      let contador = 0;
      let novaPos = 0;
      for (let i = 0; i < formatado.length; i++) {
        if (/\d/.test(formatado[i])) {
          contador++;
          if (contador === digitosAntesCursor) {
            novaPos = i + 1;
            break;
          }
        }
        novaPos = i + 1;
      }
      
      input.setSelectionRange(Math.min(novaPos, formatado.length), Math.min(novaPos, formatado.length));
    }, 0);
    
    // Limpar erro ao digitar
    if (errors.chave_pix) {
      setErrors((prev) => {
        const novos = { ...prev };
        delete novos.chave_pix;
        return novos;
      });
    }
  };

  // Validar código de barras do boleto
  const validarCodigoBarras = (codigo) => {
    if (!codigo || !codigo.trim()) return { valido: false, mensagem: 'Código de barras é obrigatório para boleto' };
    
    // Remover formatação (pontos, traços, espaços)
    const apenasDigitos = codigo.replace(/\D/g, '');
    
    // Boleto pode ter 44 dígitos (código de barras) ou 47 dígitos (linha digitável)
    if (apenasDigitos.length === 44 || apenasDigitos.length === 47) {
      // Validar se são apenas números
      if (/^\d+$/.test(apenasDigitos)) {
        return { valido: true };
      }
    }
    
    return { 
      valido: false, 
      mensagem: 'Código de barras inválido. Deve conter 44 ou 47 dígitos numéricos' 
    };
  };

  // Validar chave PIX baseado no tipo
  const validarChavePix = (chave, tipoPix) => {
    if (!chave || !chave.trim()) return { valido: false, mensagem: 'Chave PIX é obrigatória' };
    
    const apenasDigitos = chave.replace(/\D/g, '');
    const chaveLimpa = chave.trim().toLowerCase();
    
    switch (tipoPix) {
      case 'CPF':
        if (apenasDigitos.length !== 11) {
          return { valido: false, mensagem: 'CPF deve conter 11 dígitos' };
        }
        return { valido: true };
      
      case 'CNPJ':
        if (apenasDigitos.length !== 14) {
          return { valido: false, mensagem: 'CNPJ deve conter 14 dígitos' };
        }
        return { valido: true };
      
      case 'Telefone':
        if (apenasDigitos.length < 10 || apenasDigitos.length > 11) {
          return { valido: false, mensagem: 'Telefone deve conter 10 ou 11 dígitos' };
        }
        return { valido: true };
      
      case 'Email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(chaveLimpa)) {
          return { valido: false, mensagem: 'Email inválido' };
        }
        return { valido: true };
      
      case 'Aleatoria':
        // Chave aleatória geralmente é um UUID (32 caracteres hexadecimais, com ou sem hífens)
        const keyLimpaSemHifens = chaveLimpa.replace(/-/g, '');
        if (keyLimpaSemHifens.length < 32 || keyLimpaSemHifens.length > 36) {
          return { valido: false, mensagem: 'Chave aleatória inválida. Deve ser um UUID válido' };
        }
        if (!/^[0-9a-f]+$/i.test(keyLimpaSemHifens)) {
          return { valido: false, mensagem: 'Chave aleatória contém caracteres inválidos' };
        }
        return { valido: true };
      
      default:
        return { valido: false, mensagem: 'Tipo de chave PIX inválido' };
    }
  };

  const validate = () => {
    const errs = {};
    camposObrigatorios.forEach((c) => {
      const v = form[c];
      if (v === undefined || v === null || String(v).trim() === '') errs[c] = 'Obrigatório';
    });
    
    // Validação condicional para código de barras quando tipo_pagamento for "boleto"
    if (form.tipo_pagamento?.toLowerCase() === 'boleto') {
      if (form.codigo_barras) {
        const validacao = validarCodigoBarras(form.codigo_barras);
        if (!validacao.valido) {
          errs.codigo_barras = validacao.mensagem;
        }
      } else {
        errs.codigo_barras = 'Código de barras é obrigatório para boleto';
      }
    }
    
    // Validação condicional para chave PIX quando tipo_pagamento for "pix"
    if (form.tipo_pagamento?.toLowerCase() === 'pix') {
      if (form.tipo_pix) {
        if (form.chave_pix) {
          const validacao = validarChavePix(form.chave_pix, form.tipo_pix);
          if (!validacao.valido) {
            errs.chave_pix = validacao.mensagem;
          }
        } else {
          errs.chave_pix = 'Chave PIX é obrigatória';
        }
      } else {
        if (!form.chave_pix) {
          errs.chave_pix = 'Chave PIX é obrigatória';
        }
      }
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Comprimir imagem
  const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.8) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = document.createElement('img');
        img.src = e.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionar se necessário
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(file); // Se falhar, retorna arquivo original
                return;
              }
              resolve(blob);
            },
            file.type,
            quality
          );
        };
        img.onerror = () => resolve(file); // Se falhar, retorna original
      };
      reader.onerror = () => resolve(file); // Se falhar, retorna original
    });
  };

  const allowedMime = new Set(['application/pdf','image/png','image/jpeg','image/jpg','image/gif','image/webp']);
  // Limite de tamanho: 8MB (arquivo original) - base64 aumenta ~33%, então 8MB vira ~10.6MB
  // Aumentado para permitir PDFs maiores
  const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB
  
  const onFilesChange = async (e) => {
    if (!canUploadAttachments) {
      e.target.value = '';
      return;
    }

    const files = Array.from(e.target.files || []);
    const novos = [];
    
    for (const f of files) {
      if (!allowedMime.has(f.type)) {
        novos.push({ file: f, descricao: '', base64: null, error: 'Apenas PDF/imagem', ready: false });
        continue;
      }
      
      // Validar tamanho do arquivo
      if (f.size > MAX_FILE_SIZE) {
        const tamanhoMB = (f.size / 1024 / 1024).toFixed(2);
        novos.push({ 
          file: f, 
          descricao: '', 
          base64: null, 
          error: `Arquivo muito grande (${tamanhoMB} MB). Máximo: 4 MB`, 
          ready: false 
        });
        continue;
      }
      
      try {
        let fileToProcess = f;
        
        // Comprimir imagens (não PDFs)
        if (f.type.startsWith('image/') && f.type !== 'image/gif') {
          fileToProcess = await compressImage(f);
          
          // Verificar se após compressão ainda está muito grande
          if (fileToProcess.size > MAX_FILE_SIZE) {
            const tamanhoMB = (fileToProcess.size / 1024 / 1024).toFixed(2);
            novos.push({ 
              file: f, 
              descricao: '', 
              base64: null, 
              error: `Arquivo muito grande após compressão (${tamanhoMB} MB). Máximo: 4 MB`, 
              ready: false 
            });
            continue;
          }
        }
        
        // Converter para base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
          reader.readAsDataURL(fileToProcess);
        });
        
        // Verificar tamanho do base64 (aproximadamente 33% maior que o original)
        const base64Size = base64.length * 0.75; // Aproximação do tamanho original
        
        novos.push({ 
          file: f, 
          descricao: '', 
          base64, 
          error: base64 ? null : 'Falha ao ler', 
          ready: false,
          compressed: fileToProcess !== f,
          base64Size: base64Size
        });
      } catch (error) {
        novos.push({ file: f, descricao: '', base64: null, error: 'Falha ao processar arquivo', ready: false });
      }
    }
    setArquivos((prev) => [...prev, ...novos]);
    
    // Limpar input
    e.target.value = '';
  };

  const marcarPronto = (idx) => {
    setArquivos(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const descOk = (item.descricao || '').trim().length > 0;
      return { ...item, error: descOk ? null : 'Informe a descrição', ready: descOk };
    }));
  };

  const editarArquivo = (idx) => {
    setArquivos(prev => prev.map((item, i) => i === idx ? { ...item, ready: false } : item));
  };

  // Máscara/normalização de valor (BRL)
  const formatValorMask = (val) => {
    const digits = String(val || '').replace(/\D+/g, '');
    if (!digits) return '';
    const cents = digits.slice(-2).padStart(2, '0');
    let whole = digits.slice(0, -2) || '0';
    whole = whole.replace(/^0+(?=\d)/, '');
    whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${whole},${cents}`;
  };

  const normalizeValorToDecimal = (formatted) => {
    const digits = String(formatted || '').replace(/\D+/g, '');
    if (!digits) return '';
    return (parseInt(digits, 10) / 100).toFixed(2); // 1234.56
  };

  const onValorChange = (e) => {
    const masked = formatValorMask(e.target.value);
    setForm((p) => ({ ...p, valor: masked }));
  };

  // Máscara para código de barras/linha digitável
  const formatarCodigoBarras = (valor) => {
    // Remove tudo que não é dígito
    const apenasDigitos = String(valor || '').replace(/\D/g, '');
    
    if (!apenasDigitos) return '';
    
    // Limita a 47 dígitos (máximo para linha digitável)
    const digitosLimitados = apenasDigitos.slice(0, 47);
    
    // Se tiver 44 dígitos ou menos (código de barras), formata em grupos de 5
    if (digitosLimitados.length <= 44) {
      // Formata em grupos de 5: XXXXX XXXXX XXXXX...
      return digitosLimitados.replace(/(\d{5})(?=\d)/g, '$1 ');
    }
    
    // Se tiver 47 dígitos (linha digitável), formata: XXXXX.XXXXX XXXXX.XXXXXX XXXXX.XXXXXX X XXXXXXXXXXXXXX
    // Padrão da linha digitável: 5.5 5.6 5.6 1 14
    let resultado = digitosLimitados.slice(0, 5); // Primeiros 5 dígitos
    
    if (digitosLimitados.length > 5) {
      resultado += '.' + digitosLimitados.slice(5, 10); // Próximos 5 dígitos
    }
    
    if (digitosLimitados.length > 10) {
      resultado += ' ' + digitosLimitados.slice(10, 15); // Próximos 5 dígitos
    }
    
    if (digitosLimitados.length > 15) {
      resultado += '.' + digitosLimitados.slice(15, 21); // Próximos 6 dígitos
    }
    
    if (digitosLimitados.length > 21) {
      resultado += ' ' + digitosLimitados.slice(21, 26); // Próximos 5 dígitos
    }
    
    if (digitosLimitados.length > 26) {
      resultado += '.' + digitosLimitados.slice(26, 32); // Próximos 6 dígitos
    }
    
    if (digitosLimitados.length > 32) {
      resultado += ' ' + digitosLimitados.slice(32, 33); // Dígito verificador (1 dígito)
    }
    
    if (digitosLimitados.length > 33) {
      resultado += ' ' + digitosLimitados.slice(33, 47); // Últimos 14 dígitos
    }
    
    return resultado;
  };

  const onCodigoBarrasKeyDown = (e) => {
    // Interceptar backspace apenas quando o caractere antes do cursor é de formatação
    if (e.key === 'Backspace') {
      const input = e.target;
      const valorAtual = input.value;
      const posicaoCursor = input.selectionStart;
      const posicaoFim = input.selectionEnd;
      
      // Se há seleção, deixar o navegador deletar normalmente
      if (posicaoCursor !== posicaoFim) {
        return;
      }
      
      if (posicaoCursor === 0) return; // Já está no início
      
      const caractereAntes = valorAtual[posicaoCursor - 1];
      
      // Se o caractere antes do cursor é um dígito, deixar o navegador deletar normalmente
      if (/\d/.test(caractereAntes)) {
        return; // Deixa o comportamento padrão do navegador
      }
      
      // Se é um caractere de formatação, interceptar e remover ele + o dígito antes
      e.preventDefault();
      e.stopPropagation();
      
      const antesDoCursor = valorAtual.slice(0, posicaoCursor - 1);
      const depoisDoCursor = valorAtual.slice(posicaoCursor);
      
      // Encontrar o último dígito antes do cursor
      let ultimoDigitoIdx = -1;
      for (let i = antesDoCursor.length - 1; i >= 0; i--) {
        if (/\d/.test(antesDoCursor[i])) {
          ultimoDigitoIdx = i;
          break;
        }
      }
      
      // Se encontrou um dígito, remover ele e o caractere de formatação
      if (ultimoDigitoIdx >= 0) {
        const novoValor = antesDoCursor.slice(0, ultimoDigitoIdx) + depoisDoCursor;
        const formatado = formatarCodigoBarras(novoValor);
        setForm((p) => ({ ...p, codigo_barras: formatado }));
        
        // Ajustar cursor após formatação
        setTimeout(() => {
          if (!input || input.value !== formatado) return;
          const digitosAntes = novoValor.slice(0, ultimoDigitoIdx).replace(/\D/g, '').length;
          let contador = 0;
          let novaPos = 0;
          for (let i = 0; i < formatado.length; i++) {
            if (/\d/.test(formatado[i])) {
              contador++;
              if (contador === digitosAntes) {
                novaPos = i + 1;
                break;
              }
            }
            novaPos = i + 1;
          }
          input.setSelectionRange(Math.min(novaPos, formatado.length), Math.min(novaPos, formatado.length));
        }, 0);
      } else {
        // Se não há dígito antes, apenas remover o caractere de formatação
        const novoValor = antesDoCursor + depoisDoCursor;
        const formatado = formatarCodigoBarras(novoValor);
        setForm((p) => ({ ...p, codigo_barras: formatado }));
      }
    }
  };

  const onCodigoBarrasChange = (e) => {
    const input = e.target;
    const valor = input.value;
    const posicaoCursor = input.selectionStart;
    
    // Sempre formatar, independente de ser inserção ou deleção
    const formatado = formatarCodigoBarras(valor);
    setForm((p) => ({ ...p, codigo_barras: formatado }));
    
    // Ajustar cursor após formatação
    setTimeout(() => {
      if (!input || input.value !== formatado) return;
      // Calcular quantos dígitos existem antes da posição do cursor original
      const digitosAntesCursor = valor.slice(0, posicaoCursor).replace(/\D/g, '').length;
      
      // Encontrar a posição equivalente no texto formatado
      let contador = 0;
      let novaPos = 0;
      for (let i = 0; i < formatado.length; i++) {
        if (/\d/.test(formatado[i])) {
          contador++;
          if (contador === digitosAntesCursor) {
            novaPos = i + 1;
            break;
          }
        }
        novaPos = i + 1;
      }
      
      input.setSelectionRange(Math.min(novaPos, formatado.length), Math.min(novaPos, formatado.length));
    }, 0);
    
    // Limpar erro ao digitar
    if (errors.codigo_barras) {
      setErrors((prev) => {
        const novos = { ...prev };
        delete novos.codigo_barras;
        return novos;
      });
    }
  };

  // Capitalizar primeira letra do tipo_pagamento
  const capitalizeFirst = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Mapear tipo PIX do formulário para o formato da API
  const mapearTipoPixParaAPI = (tipoPix) => {
    if (!tipoPix) return '';
    
    const mapeamento = {
      'CPF': 'CPF_CNPJ',
      'CNPJ': 'CPF_CNPJ',
      'Telefone': 'CELULAR',
      'Email': 'EMAIL',
      'Aleatoria': 'ALEATORIA'
    };
    
    return mapeamento[tipoPix] || tipoPix;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    // Validar descrições dos arquivos
    if (canUploadAttachments && arquivos.length > 0) {
      let ok = true;
      const ajustados = arquivos.map(a => {
        const needs = a.base64 && (!a.descricao || !a.descricao.trim());
        if (needs) { ok = false; return { ...a, error: 'Informe a descrição', ready: false }; }
        return a;
      });
      if (!ok) { setArquivos(ajustados); return; }
    }
    
    setSaving(true);
    setUploadProgress({
      total: canUploadAttachments ? arquivos.length : 0,
      current: 0,
      uploading: true,
    });
    
    try {
      const tipoPagamentoLower = (form.tipo_pagamento || '').toLowerCase();
      
      // Construir payload base (SEM arquivos inicialmente)
      const payloadBase = {
        id_fornecedor: form.id_fornecedor,
        data_emissao: form.data_emissao,
        data_vencimento: form.data_vencimento,
        valor: normalizeValorToDecimal(form.valor),
        id_conta: form.id_conta,
        tipo_pagamento: capitalizeFirst(form.tipo_pagamento),
        documento: form.documento || '',
        numero_nota: form.numero_nota || '',
        id_contas: form.id_contas,
        previsao: form.previsao,
        obs: form.obs || '',
        status: form.status,
        empresa,
      };
      
      // Campos condicionais para PIX
      if (tipoPagamentoLower === 'pix') {
        if (form.chave_pix) payloadBase.chave_pix = form.chave_pix;
        if (form.tipo_pix) {
          // Mapear tipo PIX para o formato esperado pela API
          payloadBase.tipo_pix = mapearTipoPixParaAPI(form.tipo_pix);
        }
      }
      
      // Campo condicional para Boleto
      if (tipoPagamentoLower === 'boleto' && form.codigo_barras) {
        payloadBase.codigo_barras = form.codigo_barras;
      }
      
      // Preparar arquivos validados
      const arquivosValidados = canUploadAttachments
        ? arquivos.filter(a => a.base64 && !a.error).map(a => ({
            descricao: (a.descricao || a.file.name).trim(),
            arquivo_base64: a.base64
          }))
        : [];
      
      // Primeiro, criar a conta SEM arquivos (sempre)
      const payloadSemArquivos = { ...payloadBase, arquivos: [] };
      let resp;
      
      try {
        // Criar conta primeiro
        const fingerprint = JSON.stringify(payloadSemArquivos);
        if (operationKey.current.payload !== fingerprint) {
          operationKey.current = { payload: fingerprint, key: window.crypto.randomUUID() };
        }
        resp = await api.post('/n8n/administracao/a_pagar/nova', payloadSemArquivos, {
          headers: { 'Idempotency-Key': operationKey.current.key }
        });
        
        if (!resp || resp.status >= 400) {
          throw new Error('Erro ao criar conta');
        }
        
        operationKey.current = { payload: null, key: null };
        // Extrair id_conta da resposta
        const responseData = resp.data;
        let idConta = null;
        
        // A resposta pode vir como array ou objeto
        if (Array.isArray(responseData) && responseData.length > 0) {
          idConta = responseData[0]?.id_conta || responseData[0]?.id;
        } else if (responseData?.id_conta) {
          idConta = responseData.id_conta;
        } else if (responseData?.id) {
          idConta = responseData.id;
        }
        
        if (!idConta) {
          console.error('Resposta da criação de conta:', responseData);
          throw new Error('Não foi possível obter o ID da conta criada');
        }
        
        // Agora enviar arquivos individualmente
        if (arquivosValidados.length > 0) {
          setUploadProgress({ total: arquivosValidados.length, current: 0, uploading: true });
          
          let arquivosEnviados = 0;
          let arquivosComErro = [];
          
          for (let i = 0; i < arquivosValidados.length; i++) {
            const arquivo = arquivosValidados[i];
            
            try {
              // Verificar tamanho do payload antes de enviar
              const payload = {
                id_conta: idConta,
                descricao: arquivo.descricao,
                arquivo_base64: arquivo.arquivo_base64,
                empresa,
              };
              const payloadSize = JSON.stringify(payload).length;
              
              // Limite aumentado para 40MB para o payload JSON (considerando base64)
              // O nginx está configurado para 50MB, então 40MB é um limite seguro
              const MAX_PAYLOAD_SIZE = 40 * 1024 * 1024; // 40MB
              
              if (payloadSize > MAX_PAYLOAD_SIZE) {
                const tamanhoMB = (payloadSize / 1024 / 1024).toFixed(2);
                arquivosComErro.push({
                  nome: arquivo.descricao,
                  erro: `Arquivo muito grande (${tamanhoMB} MB). Tamanho máximo: ~30 MB por arquivo`
                });
                continue;
              }
              
              await api.post('/n8n/administracao/a_pagar/novo_arquivo', {
                id_conta: idConta,
                descricao: arquivo.descricao,
                arquivo_base64: arquivo.arquivo_base64,
                empresa,
              }, {
                timeout: 60000, // 60 segundos de timeout para arquivos grandes
                maxContentLength: Infinity,
                maxBodyLength: Infinity,
              });
              
              arquivosEnviados++;
              setUploadProgress({ 
                total: arquivosValidados.length, 
                current: arquivosEnviados, 
                uploading: true 
              });
            } catch (arquivoError) {
              console.error(`Erro ao enviar arquivo ${i + 1}:`, arquivoError);
              
              let mensagemErro = arquivoError.response?.data?.message || arquivoError.message || 'Erro desconhecido';
              
              // Mensagem mais amigável para erro 413
              if (arquivoError.response?.status === 413) {
                const arquivoOriginal = arquivos.find(a => a.descricao === arquivo.descricao);
                const tamanhoMB = arquivoOriginal ? (arquivoOriginal.file.size / 1024 / 1024).toFixed(2) : 'desconhecido';
                const payloadSize = arquivo.arquivo_base64 ? (arquivo.arquivo_base64.length * 0.75 / 1024 / 1024).toFixed(2) : 'desconhecido';
                console.error('Erro 413 - Detalhes:', {
                  arquivoOriginalMB: tamanhoMB,
                  payloadEstimadoMB: payloadSize,
                  base64Length: arquivo.arquivo_base64?.length || 0,
                  responseHeaders: arquivoError.response?.headers,
                });
                mensagemErro = `Arquivo muito grande (${tamanhoMB} MB original, ~${payloadSize} MB no payload). Por favor, reduza o tamanho do arquivo.`;
              }
              
              arquivosComErro.push({
                nome: arquivo.descricao,
                erro: mensagemErro
              });
            }
          }
          
          setUploadProgress({ 
            total: arquivosValidados.length, 
            current: arquivosValidados.length, 
            uploading: false 
          });
          
          // Avisar se houver arquivos com erro
          if (arquivosComErro.length > 0) {
            const mensagemErro = `Conta criada com sucesso!\n\n` +
              `Arquivos enviados: ${arquivosEnviados} de ${arquivosValidados.length}\n\n` +
              `Arquivos com erro:\n${arquivosComErro.map(a => `- ${a.nome}: ${a.erro}`).join('\n')}`;
            alert(mensagemErro);
          }
        }
        
        onCreated?.();
        onClose();
        
      } catch (error) {
        console.error('Erro ao criar conta:', error);
        throw error;
      }
    } catch (error) {
      console.error('Erro ao criar conta:', error);
      if (error.response?.status === 413) {
        alert('Os arquivos são muito grandes. Por favor, reduza o tamanho ou quantidade de arquivos e tente novamente.');
      } else {
        alert('Erro ao criar conta a pagar. Por favor, tente novamente.');
      }
    } finally { 
      setSaving(false);
      setUploadProgress({ total: 0, current: 0, uploading: false });
    }
  };

  const bind = (name) => ({
    name,
    value: form[name],
    onChange: (e) => setForm((p) => ({ ...p, [name]: e.target ? e.target.value : e })),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nova Conta a Pagar" size="xl">
      <form onSubmit={onSubmit} className="space-y-6">
        {/* Seção: Informações Principais */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-[color:var(--border)]">
            <FileText className="h-5 w-5 text-indigo-600" />
            <h4 className="text-base font-semibold text-[color:var(--text-primary)]">Informações Principais</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                Fornecedor <span className="text-red-500">*</span>
              </label>
            <SearchableSelect
              options={fornOpts}
              loading={loadingLists}
              value={fornOpts.find(o => o.id === form.id_fornecedor) || null}
              onChange={(opt) => setForm((p) => ({ ...p, id_fornecedor: opt ? opt.id : '' }))}
              placeholder="Buscar fornecedor por nome, ID ou CPF/CNPJ"
              searchFields={['label','id','cpf_cnpj']}
              error={errors.id_fornecedor}
            />
          </div>
          <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                Conta Bancária <span className="text-red-500">*</span>
              </label>
            <SearchableSelect
              options={contaOpts}
              loading={loadingLists}
                value={contaOpts.find(o => o.id === form.id_contas) || null}
                onChange={(opt) => setForm((p) => ({ ...p, id_contas: opt ? opt.id : '' }))}
              placeholder="Buscar conta por nome ou ID"
                error={errors.id_contas}
            />
          </div>
          <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                Plano (Planejamento) <span className="text-red-500">*</span>
              </label>
            <SearchableSelect
              options={planoOpts}
              loading={loadingLists}
                value={planoOpts.find(o => o.id === form.id_conta) || null}
                onChange={(opt) => setForm((p) => ({ ...p, id_conta: opt ? opt.id : '' }))}
              placeholder="Buscar plano por descrição ou ID"
                error={errors.id_conta}
            />
          </div>
          
          <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                Data Emissão <span className="text-red-500">*</span>
              </label>
              <input 
                type="date" 
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent ${
                  errors.data_emissao ? 'border-red-300 bg-red-50' : 'border-[color:var(--border)]'
                }`}
                {...bind('data_emissao')} 
              />
            {errors.data_emissao && <p className="mt-1 text-sm text-red-600">{errors.data_emissao}</p>}
          </div>
          <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                Data Vencimento <span className="text-red-500">*</span>
              </label>
              <input 
                type="date" 
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent ${
                  errors.data_vencimento ? 'border-red-300 bg-red-50' : 'border-[color:var(--border)]'
                }`}
                {...bind('data_vencimento')} 
              />
            {errors.data_vencimento && <p className="mt-1 text-sm text-red-600">{errors.data_vencimento}</p>}
          </div>
          <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                Valor <span className="text-red-500">*</span>
              </label>
            <input
              type="text"
              inputMode="numeric"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent ${
                  errors.valor ? 'border-red-300 bg-red-50' : 'border-[color:var(--border)]'
                }`}
              name="valor"
              value={form.valor}
              onChange={onValorChange}
              placeholder="0,00"
            />
            {errors.valor && <p className="mt-1 text-sm text-red-600">{errors.valor}</p>}
          </div>
          <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                Tipo de Pagamento <span className="text-red-500">*</span>
              </label>
              <select 
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent ${
                  errors.tipo_pagamento ? 'border-red-300 bg-red-50' : 'border-[color:var(--border)]'
                }`}
                {...bind('tipo_pagamento')}
              >
              <option value="">Selecione</option>
              <option value="boleto">Boleto</option>
              <option value="pix">PIX</option>
              <option value="transferencia">Transferência</option>
              <option value="dinheiro">Dinheiro</option>
              <option value="cartao">Cartão</option>
            </select>
            {errors.tipo_pagamento && <p className="mt-1 text-sm text-red-600">{errors.tipo_pagamento}</p>}
          </div>
          
            {/* Campos condicionais para PIX */}
            {form.tipo_pagamento?.toLowerCase() === 'pix' && (
              <>
          <div>
                  <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                    Tipo PIX <span className="text-red-500">*</span>
                  </label>
                  <select 
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent ${
                      errors.tipo_pix ? 'border-red-300 bg-red-50' : 'border-[color:var(--border)]'
                    }`}
                    value={form.tipo_pix || ''}
                    onChange={(e) => {
                      const novoTipo = e.target.value;
                      setForm((p) => ({ ...p, tipo_pix: novoTipo, chave_pix: '' })); // Limpa chave ao mudar tipo
                      if (errors.tipo_pix || errors.chave_pix) {
                        setErrors((prev) => {
                          const novos = { ...prev };
                          delete novos.tipo_pix;
                          delete novos.chave_pix;
                          return novos;
                        });
                      }
                    }}
                  >
                    <option value="">Selecione o tipo</option>
                    <option value="CPF">CPF</option>
                    <option value="CNPJ">CNPJ</option>
                    <option value="Email">Email</option>
                    <option value="Telefone">Telefone</option>
                    <option value="Aleatoria">Aleatória</option>
                  </select>
                  {errors.tipo_pix && <p className="mt-1 text-sm text-red-600">{errors.tipo_pix}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                    Chave PIX <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text" 
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent ${
                      errors.chave_pix ? 'border-red-300 bg-red-50' : 'border-[color:var(--border)]'
                    } ${!form.tipo_pix ? 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]' : ''}`}
                    name="chave_pix"
                    value={form.chave_pix || ''}
                    placeholder={
                      form.tipo_pix === 'CPF' ? 'Digite ou cole o CPF (XXX.XXX.XXX-XX)' :
                      form.tipo_pix === 'CNPJ' ? 'Digite ou cole o CNPJ (XX.XXX.XXX/XXXX-XX)' :
                      form.tipo_pix === 'Telefone' ? 'Digite ou cole o telefone ((XX) XXXXX-XXXX)' :
                      form.tipo_pix === 'Email' ? 'Digite ou cole o email' :
                      form.tipo_pix === 'Aleatoria' ? 'Digite ou cole a chave aleatória (UUID)' :
                      'Selecione o tipo PIX primeiro'
                    }
                    disabled={!form.tipo_pix}
                    onKeyDown={onChavePixKeyDown}
                    onChange={onChavePixChange}
                  />
                  {errors.chave_pix && (
                    <p className="mt-1 text-sm text-red-600">{errors.chave_pix}</p>
                  )}
                  {form.tipo_pix && (
                    <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                      {form.tipo_pix === 'CPF' && 'Formato: XXX.XXX.XXX-XX'}
                      {form.tipo_pix === 'CNPJ' && 'Formato: XX.XXX.XXX/XXXX-XX'}
                      {form.tipo_pix === 'Telefone' && 'Formato: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX'}
                      {form.tipo_pix === 'Email' && 'Digite um email válido'}
                      {form.tipo_pix === 'Aleatoria' && 'Formato: UUID (com ou sem hífens)'}
                    </p>
                  )}
                </div>
              </>
            )}
            
            {/* Campo condicional para Boleto */}
            {form.tipo_pagamento?.toLowerCase() === 'boleto' && (
              <div>
                <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                  Código de Barras <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text" 
                  name="codigo_barras"
                  value={form.codigo_barras || ''}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent ${
                    errors.codigo_barras ? 'border-red-300 bg-red-50' : 'border-[color:var(--border)]'
                  }`}
                  placeholder="Digite ou cole o código de barras"
                  onKeyDown={onCodigoBarrasKeyDown}
                  onChange={onCodigoBarrasChange}
                  maxLength={59} // 47 dígitos + formatação (pontos e espaços)
                />
                {errors.codigo_barras && (
                  <p className="mt-1 text-sm text-red-600">{errors.codigo_barras}</p>
                )}
                <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                  Aceita código de barras (44 dígitos) ou linha digitável (47 dígitos) com ou sem formatação
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                Regime contábil (Previsão) <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap items-center gap-4 p-3 bg-[color:var(--surface-muted)] rounded-lg border border-[color:var(--border)]">
                <label className="inline-flex items-center gap-2 text-sm text-[color:var(--text-primary)] cursor-pointer hover:text-[color:var(--text-primary)]">
                <input
                  type="radio"
                  name="previsao"
                  value="N"
                  checked={form.previsao === 'N'}
                  onChange={(e) => setForm((p) => ({ ...p, previsao: e.target.value }))}
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 focus:ring-2"
                />
                Competência (Previsão não)
              </label>
                <label className="inline-flex items-center gap-2 text-sm text-[color:var(--text-primary)] cursor-pointer hover:text-[color:var(--text-primary)]">
                <input
                  type="radio"
                  name="previsao"
                  value="S"
                  checked={form.previsao === 'S'}
                  onChange={(e) => setForm((p) => ({ ...p, previsao: e.target.value }))}
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 focus:ring-2"
                />
                Caixa (Previsão sim)
              </label>
                <label className="inline-flex items-center gap-2 text-sm text-[color:var(--text-primary)] cursor-pointer hover:text-[color:var(--text-primary)]">
                <input
                  type="radio"
                  name="previsao"
                  value="M"
                  checked={form.previsao === 'M'}
                  onChange={(e) => setForm((p) => ({ ...p, previsao: e.target.value }))}
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 focus:ring-2"
                />
                Manual
              </label>
            </div>
            {errors.previsao && <p className="mt-1 text-sm text-red-600">{errors.previsao}</p>}
          </div>
            <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <select 
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent ${
                  errors.status ? 'border-red-300 bg-red-50' : 'border-[color:var(--border)]'
                }`}
                {...bind('status')}
              >
                <option value="">Selecione</option>
                <option value="A">A pagar</option>
                <option value="F">Pago</option>
                <option value="P">Parcial</option>
              </select>
              {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status}</p>}
          </div>
          </div>
        </div>

        {/* Seção: Informações Adicionais */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-[color:var(--border)]">
            <FileText className="h-5 w-5 text-indigo-600" />
            <h4 className="text-base font-semibold text-[color:var(--text-primary)]">Informações Adicionais</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">Documento</label>
              <input 
                className="w-full px-3 py-2 border border-[color:var(--border)] rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent" 
                {...bind('documento')} 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">Número da Nota</label>
              <input 
                className="w-full px-3 py-2 border border-[color:var(--border)] rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent" 
                {...bind('numero_nota')} 
              />
          </div>
          <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">Observação</label>
              <textarea 
                rows={3}
                className="w-full px-3 py-2 border border-[color:var(--border)] rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent" 
                {...bind('obs')} 
              />
            </div>
          </div>
        </div>

        {/* Seção: Arquivos */}
        {canUploadAttachments && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-[color:var(--border)]">
            <Paperclip className="h-5 w-5 text-indigo-600" />
            <h4 className="text-base font-semibold text-[color:var(--text-primary)]">Anexos</h4>
            {arquivos.length > 0 && (
              <span className="ml-auto text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">
                {arquivos.filter(a => a.ready).length} de {arquivos.length} pronto{arquivos.filter(a => a.ready).length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          
          {/* Área de Upload */}
          <div>
            <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-3">Arquivos (PDF e imagens)</label>
            <div className="relative">
              <div className="border-2 border-dashed border-[color:var(--border)] rounded-xl p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-all duration-200 group">
                <input 
                  type="file" 
                  accept="application/pdf,image/*" 
                  multiple 
                  onChange={onFilesChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  id="file-upload"
                />
                <div className="pointer-events-none">
                  <div className="flex justify-center mb-3">
                    <div className="p-3 bg-indigo-100 rounded-full group-hover:bg-indigo-200 transition-colors">
                      <Upload className="h-6 w-6 text-indigo-600" />
                    </div>
                  </div>
                  <p className="text-sm font-medium text-[color:var(--text-primary)] mb-1">
                    Clique para selecionar ou arraste os arquivos aqui
                  </p>
                  <p className="text-xs text-[color:var(--text-muted)]">
                    Formatos aceitos: PDF, PNG, JPG, JPEG, GIF, WEBP | Tamanho máximo: 8 MB por arquivo
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Arquivos */}
          {arquivos.length > 0 && (
            <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
              {arquivos.map((a, idx) => {
                const getFileIcon = () => {
                  const fileName = a.file.name.toLowerCase();
                  if (fileName.endsWith('.pdf')) {
                    return <FileText className="h-6 w-6 text-red-500" />;
                  }
                  if (['.png', '.jpg', '.jpeg', '.gif', '.webp'].some(ext => fileName.endsWith(ext))) {
                    return <ImageIcon className="h-6 w-6 text-blue-500" />;
                  }
                  return <File className="h-6 w-6 text-[color:var(--text-muted)]" />;
                };

                const formatFileSize = (bytes) => {
                  if (bytes < 1024) return bytes + ' B';
                  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
                };

                return (
                  <div 
                    key={idx} 
                    className={`relative flex items-start gap-4 border rounded-xl p-4 bg-[color:var(--surface)] transition-all ${
                      a.ready 
                        ? 'border-green-200 bg-green-50/30 shadow-sm' 
                        : 'border-[color:var(--border)] hover:border-[color:var(--border)] hover:shadow-md'
                    }`}
                  >
                    {/* Ícone do Arquivo */}
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="p-2 bg-[color:var(--surface)] rounded-lg border border-[color:var(--border)] shadow-sm">
                        {getFileIcon()}
                      </div>
                    </div>

                    {/* Informações do Arquivo */}
                  <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="text-sm font-semibold text-[color:var(--text-primary)] truncate" title={a.file.name}>
                              {a.file.name}
                            </h5>
                            {a.ready && (
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                            )}
                  </div>
                          <p className="text-xs text-[color:var(--text-muted)]">
                            {formatFileSize(a.file.size)}
                            {a.compressed && (
                              <span className="ml-1 text-green-600 font-medium">(comprimido)</span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Campo de Descrição */}
                      <div className="mt-3">
                    <input
                          type="text"
                          placeholder="Adicione uma descrição para o arquivo"
                      value={a.descricao}
                      disabled={a.ready}
                      onChange={(e) => setArquivos(prev => prev.map((x,i)=> i===idx?{...x, descricao:e.target.value, error: null}:x))}
                          className={`w-full border rounded-lg px-3 py-2 text-sm transition-colors ${
                            a.ready 
                              ? 'bg-[color:var(--surface)] border-[color:var(--border)] text-[color:var(--text-muted)] cursor-not-allowed' 
                              : 'border-[color:var(--border)] focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                          } ${a.error ? 'border-red-300 bg-red-50' : ''}`}
                        />
                        {a.error && (
                          <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                            <X className="h-3 w-3" />
                            {a.error}
                          </p>
                        )}
                  </div>
                    </div>

                    {/* Ações */}
                    <div className="flex items-start gap-2 flex-shrink-0">
                    {!a.ready ? (
                        <button 
                          type="button" 
                          onClick={() => marcarPronto(idx)}
                          disabled={!a.descricao?.trim()}
                          className="px-3 py-2 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                        >
                          <CheckCircle className="h-3.5 w-3.5" />
                          Confirmar
                        </button>
                      ) : (
                        <button 
                          type="button" 
                          onClick={() => editarArquivo(idx)} 
                          className="px-3 py-2 text-xs font-medium text-[color:var(--text-primary)] bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors flex items-center gap-1.5"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                          Editar
                        </button>
                      )}
                      <button 
                        type="button" 
                        onClick={() => setArquivos(prev => prev.filter((_,i)=>i!==idx))} 
                        className="px-3 py-2 text-xs font-medium text-red-600 bg-[color:var(--surface)] border border-red-200 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors flex items-center gap-1.5"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Remover
                      </button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
          </div>
        )}

        {/* Footer com botões */}
        <div className="flex justify-end gap-3 pt-4 border-t border-[color:var(--border)]">
          <button 
            type="button" 
            onClick={onClose} 
            className="px-6 py-2.5 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            disabled={saving} 
            className="px-6 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {uploadProgress.uploading && uploadProgress.total > 0 
                  ? `Enviando arquivos... (${uploadProgress.current}/${uploadProgress.total})`
                  : 'Salvando...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Criar Conta a Pagar
              </>
            )}
          </button>
          {uploadProgress.uploading && uploadProgress.total > 0 && (
            <div className="w-full mt-2">
              <div className="w-full bg-[color:var(--surface-muted)] rounded-full h-2">
                <div 
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                />
        </div>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}


